import { useState } from 'react';

import { useCurrentUser } from '@/hooks';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
} from '@/ui';
import { isValidLinkedin, sanitizeUserProfileUpdate } from '@/utils';

import BragSheet from './BragSheet';
import Certifications from './Certifications';
import Profile from './Profile';
import Roles from './Roles';
import Styles from './Styles';

import type { CoachProfile, User, UserEmployment, UserProfile } from '@/types';
import type { FC } from 'react';

import Experience from '@/components/Profile/Coach/Edit/Coach/Experience';
import Bio from '@/components/Profile/Edit/Bio';
import { useUpdateCoachProfileMutation } from '@/store/api/coachAssignmentApi';
import {
  useBulkUpdateUserEmploymentMutation,
  useUpdateUserMutation,
  useUpdateUserProfileMutation,
} from '@/store/api/userApi';
import { isAdmin, isCoach, isSupport } from '@/types/user';

const isValidDatePart = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return false;
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

interface EditCoachProps {
  coach: User;
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we don't actually use the result
  refetchCoach: () => Promise<any>;
}

const EditCoach: FC<EditCoachProps> = ({
  coach: currentCoach,
  onClose,
  refetchCoach,
}) => {
  const { user: currentUser } = useCurrentUser();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [certificationDatesValid, setCertificationDatesValid] = useState(true);
  const [certificationRevision, setCertificationRevision] = useState(0);
  const [coach, setCoach] = useState(currentCoach);
  const [profile, setProfile] = useState(
    currentCoach.profile ?? ({} as UserProfile),
  );
  const [coachProfile, setCoachProfile] = useState(
    currentCoach.coachProfile ?? ({} as CoachProfile),
  );
  const [employments, setEmployments] = useState(
    currentCoach.userEmployments ?? [],
  );

  const [updateUser] = useUpdateUserMutation();
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [updateCoachProfile] = useUpdateCoachProfileMutation();
  const [bulkUpdateUserEmployment] = useBulkUpdateUserEmploymentMutation();

  const onChange = (changed: User) => setCoach(changed);
  const onProfileChange = (changed: UserProfile) => setProfile(changed);
  const onCoachProfileChange = (changed: CoachProfile) =>
    setCoachProfile(changed);
  const onCompanyChange = (changed: UserEmployment[]) =>
    setEmployments(changed);
  const onCancel = () => onClose();
  const canEditCertifications =
    isAdmin(currentUser) ||
    (isCoach(currentUser) &&
      currentUser.id === currentCoach.id &&
      !isSupport(currentUser));

  const onSave = async () => {
    setIsSaving(true);
    setSaveError(false);

    const [firstName, ...rest] = coach.fullName.split(' ');
    const lastName = rest.join(' ');
    const coachToUpdate = {
      ...coach,
      firstName,
      lastName,
    };
    const coachProfileToUpdate = { ...coachProfile };
    if (canEditCertifications) {
      coachProfileToUpdate.certifications = (
        coachProfile.certifications ?? []
      ).map(({ earnedOn, name }) => ({ earnedOn, name: name.trim() }));
    } else {
      delete coachProfileToUpdate.certifications;
    }
    const settlements = await Promise.allSettled([
      updateUser({ id: coach.id, user: coachToUpdate }).unwrap(),
      updateUserProfile({
        id: coach.id,
        userProfile: sanitizeUserProfileUpdate(profile),
      }).unwrap(),
      updateCoachProfile({
        coachProfile: coachProfileToUpdate,
        id: coach.id,
      }).unwrap(),
      bulkUpdateUserEmployment({ employments }).unwrap(),
    ]);
    const allSucceeded = settlements.every(
      (settlement) => settlement.status === 'fulfilled',
    );
    try {
      const refreshed = await refetchCoach();
      if (allSucceeded) onClose();
      else {
        const refreshedCoach = refreshed?.data as User | undefined;
        if (refreshedCoach) {
          setCoach(refreshedCoach);
          setProfile(refreshedCoach.profile ?? ({} as UserProfile));
          setCoachProfile(refreshedCoach.coachProfile ?? ({} as CoachProfile));
          setEmployments(refreshedCoach.userEmployments ?? []);
          setCertificationDatesValid(true);
          setCertificationRevision((revision) => revision + 1);
        }
        setSaveError(true);
      }
    } catch {
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const certificationsValid =
    !canEditCertifications ||
    (certificationDatesValid &&
      (coachProfile.certifications ?? []).every(
        ({ earnedOn, name }) => name.trim() && isValidDatePart(earnedOn),
      ));
  const isDisabled =
    (!!profile.linkedIn && !isValidLinkedin(profile.linkedIn)) ||
    !certificationsValid;

  return (
    <Dialog maxWidth="md" fullWidth open>
      <DialogContent>
        {saveError && (
          <Alert
            aria-label="Unable to save changes"
            className="mb-6"
            severity="error"
          >
            Unable to save changes. Review the latest values and try again.
          </Alert>
        )}
        <Profile
          coach={coach}
          coachProfile={coachProfile}
          profile={profile}
          onChange={onChange}
          onCoachProfileChange={onCoachProfileChange}
          onProfileChange={onProfileChange}
        />
        <Divider className="my-8" />
        <Bio profile={profile} onProfileChange={onProfileChange} />
        <Divider className="my-8" />
        <Experience
          coachProfile={coachProfile}
          employments={employments}
          onCoachProfileChange={onCoachProfileChange}
          onCompanyChange={onCompanyChange}
        />
        <Divider className="my-8" />
        <Roles
          coachProfile={coachProfile}
          onCoachProfileChange={onCoachProfileChange}
        />
        <Divider className="my-8" />
        <Styles
          coachProfile={coachProfile}
          onCoachProfileChange={onCoachProfileChange}
        />
        <Divider className="my-8" />
        <BragSheet
          coachProfile={coachProfile}
          onCoachProfileChange={onCoachProfileChange}
        />
        {canEditCertifications && (
          <>
            <Divider className="my-8" />
            <Certifications
              key={certificationRevision}
              certifications={coachProfile.certifications ?? []}
              onChange={(certifications) =>
                onCoachProfileChange({ ...coachProfile, certifications })
              }
              onValidityChange={setCertificationDatesValid}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={isDisabled}
          loading={isSaving}
          variant="contained"
          onClick={onSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCoach;
